import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { 
  GraduationCap, Award, Shirt, Users, CheckCircle, Clock, 
  Search, Download, Camera, Loader2, Trophy, Star
} from 'lucide-react';
import { toast } from 'sonner';

export default function InstructorCompletionsPage() {
  const { api } = useAuth();
  const [sections, setSections] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [selectedSection, setSelectedSection] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get all enrollments
      const enrollmentsRes = await api.get('/enrollments/');
      const enrollments = enrollmentsRes.data.results || enrollmentsRes.data || [];
      const completedEnrollments = enrollments.filter(e => e.status === 'completed');
      
      // Group by section and get section details
      const sectionMap = {};
      completedEnrollments.forEach(e => {
        if (!sectionMap[e.section]) {
          sectionMap[e.section] = {
            id: e.section,
            course_title: e.section_details?.course_title || 'Course',
            total_sessions: 4
          };
        }
      });
      
      setSections(Object.values(sectionMap));
      
      // Fetch learner details for each unique learner
      const learnerIds = [...new Set(completedEnrollments.map(e => e.learner))];
      const learnerDetailsMap = {};
      
      // Batch fetch learners
      try {
        const learnersRes = await api.get('/learners/');
        const allLearners = learnersRes.data.results || learnersRes.data || [];
        allLearners.forEach(l => {
          learnerDetailsMap[l.id] = l;
        });
      } catch (e) {
        console.log('Could not fetch all learners, will try individually');
      }
      
      // Enrich completions with learner details from serializer
      const enrichedCompletions = completedEnrollments.map(c => ({
        ...c,
        section_title: c.section_title || sectionMap[c.section]?.course_title || 'Course',
        learner_details: learnerDetailsMap[c.learner] || {
          first_name: c.learner_name?.split(' ')[0] || 'Student',
          last_name: c.learner_name?.split(' ').slice(1).join(' ') || '',
          grade: c.learner_grade,
          tshirt_size_display: c.learner_tshirt_size
        }
      }));
      
      setCompletions(enrichedCompletions);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load completion data');
    } finally {
      setLoading(false);
    }
  };

  // Filter completions
  const filteredCompletions = completions.filter(c => {
    const matchesSection = selectedSection === 'all' || c.section_id === selectedSection;
    const learnerName = `${c.learner_details?.first_name || ''} ${c.learner_details?.last_name || ''}`.toLowerCase();
    const matchesSearch = learnerName.includes(searchTerm.toLowerCase()) || 
                          c.section_title?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSection && matchesSearch;
  });

  // Stats
  const totalCompletions = completions.length;
  const uniqueStudents = new Set(completions.map(c => c.learner)).size;
  const recentCompletions = completions.filter(c => {
    const updated = new Date(c.updated_at);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return updated > weekAgo;
  }).length;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="h-6 w-6" />
            Course Completions
          </h1>
          <p className="text-muted-foreground">View students who completed your courses</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Trophy className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{totalCompletions}</p>
                <p className="text-sm text-muted-foreground">Total Completions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{uniqueStudents}</p>
                <p className="text-sm text-muted-foreground">Students Graduated</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{recentCompletions}</p>
                <p className="text-sm text-muted-foreground">This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Award className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{totalCompletions * 100}</p>
                <p className="text-sm text-muted-foreground">Points Awarded</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by student name or course..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedSection} onValueChange={setSelectedSection}>
          <SelectTrigger className="w-full md:w-[250px]">
            <SelectValue placeholder="All Classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classes</SelectItem>
            {sections.map(section => (
              <SelectItem key={section.id} value={section.id}>
                {section.course_title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Completions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Completed Students</span>
            <Badge variant="secondary">{filteredCompletions.length} students</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCompletions.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No Completions Yet</h3>
              <p className="text-muted-foreground">
                Students will appear here once they complete all sessions
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCompletions.map((completion, idx) => (
                <div 
                  key={`${completion.id}-${idx}`}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                      {completion.learner_details?.first_name?.[0] || '?'}
                    </div>
                    <div>
                      <h4 className="font-semibold flex items-center gap-2">
                        {completion.learner_details?.first_name} {completion.learner_details?.last_name}
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </h4>
                      <p className="text-sm text-muted-foreground">{completion.section_title}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span>Grade: {completion.learner_details?.grade || 'N/A'}</span>
                        <span>•</span>
                        <span>Completed: {formatDate(completion.updated_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* T-Shirt Size */}
                    {completion.learner_details?.tshirt_size && (
                      <div className="text-center">
                        <Shirt className="h-5 w-5 mx-auto text-muted-foreground" />
                        <span className="text-xs font-medium">
                          {completion.learner_details.tshirt_size_display || completion.learner_details.tshirt_size}
                        </span>
                      </div>
                    )}
                    
                    {/* Points Badge */}
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      +100 pts
                    </Badge>
                    
                    {/* Status Badge */}
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Completed
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {filteredCompletions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export Completions
            </Button>
            <Button variant="outline" size="sm">
              <Camera className="h-4 w-4 mr-2" />
              Upload Class Photo
            </Button>
            <Button variant="outline" size="sm">
              <Award className="h-4 w-4 mr-2" />
              Print Certificates
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
